import React, { useState, useCallback, useMemo } from 'react';
import OrbitLayout from '../components/OrbitLayout';
import { useLanguage } from '../context/LanguageContext';
import { ChartBar, Play, Warning, CheckCircle, Sliders, Planet, ArrowDown, ArrowUp, Minus, TrendUp, Calendar, CurrencyDollar, PiggyBank, CaretDown, CaretRight } from '@phosphor-icons/react';
import { formatMoney } from '../utils/formatMoney';

const DEFAULT_RANK_DIST = [
  { id: 'mercure', fr: 'Mercure', en: 'Mercury', pct: 40 },
  { id: 'venus', fr: 'Venus', en: 'Venus', pct: 25 },
  { id: 'terre', fr: 'Terre', en: 'Earth', pct: 15 },
  { id: 'mars', fr: 'Mars', en: 'Mars', pct: 10 },
  { id: 'jupiter', fr: 'Jupiter', en: 'Jupiter', pct: 7 },
  { id: 'saturne', fr: 'Saturne', en: 'Saturn', pct: 3 },
];

const RANK_UNLOCK = { mercure: 12, venus: 13, terre: 13, mars: 14, jupiter: 14, saturne: 15 };
const RANK_FS = { mercure: 1, venus: 2, terre: 4, mars: 6, jupiter: 8, saturne: 10 };
const RANK_MATCH = { mercure: 1, venus: 1, terre: 2, mars: 3, jupiter: 4, saturne: 5 };
const FS_RATES = { 1: 0.50, 2: 0.10, 3: 0.05, 4: 0.05, 5: 0.03, 6: 0.02, 7: 0.02, 8: 0.01, 9: 0.01, 10: 0.01 };
const MATCH_RATES = { 1: 0.50, 2: 0.10, 3: 0.05, 4: 0.05, 5: 0.03 };

// === HELPER: Calculate matrix capacity ===
function getMaxCapacity(width, depth) {
  let c = 0;
  for (let i = 1; i <= depth; i++) c += Math.pow(width, i);
  return c;
}

// === HELPER: Matrix commissions for N members ===
function calcMatrixComm(members, commPerMember, fillPct, width, depth, rankDist, totalRankPct) {
  const fillFactor = fillPct / 100;
  const maxCap = getMaxCapacity(width, depth);
  const effective = Math.min(members, Math.round(maxCap * fillFactor));
  let total = 0;
  let distributed = 0;

  for (let orbit = 1; orbit <= depth; orbit++) {
    const slots = Math.pow(width, orbit);
    const filled = Math.min(Math.round(slots * fillFactor), effective - distributed);
    distributed += filled;

    let avgLevels = 0;
    const norm = rankDist.map(r => r.pct / (totalRankPct || 100));
    norm.forEach((pct, i) => {
      const unlocked = Math.min(RANK_UNLOCK[rankDist[i].id] || 12, depth);
      avgLevels += pct * Math.min(orbit, unlocked);
    });

    total += filled * commPerMember * avgLevels;
    if (distributed >= effective) break;
  }
  return total;
}

// === HELPER: Fast Start commissions ===
function calcFastStart(totalNewRecruits, fsBonusPerRecruit, rankDist, totalRankPct) {
  let total = 0;
  for (let level = 1; level <= 10; level++) {
    const rate = FS_RATES[level] || 0;
    let pctEligible = 0;
    rankDist.forEach(r => {
      if (RANK_FS[r.id] >= level) pctEligible += r.pct / (totalRankPct || 100);
    });
    total += totalNewRecruits * fsBonusPerRecruit * rate * pctEligible;
  }
  return total;
}

// === HELPER: Matching Bonus ===
function calcMatching(matrixComm, members, avgDirectEnrollees, rankDist, totalRankPct) {
  let total = 0;
  const avgMatrixPerMember = matrixComm / (members || 1);

  for (let gen = 1; gen <= 5; gen++) {
    const rate = MATCH_RATES[gen] || 0;
    let pctEligible = 0;
    rankDist.forEach(r => {
      if (RANK_MATCH[r.id] >= gen) pctEligible += r.pct / (totalRankPct || 100);
    });
    const enrolleesAtGen = Math.min(Math.pow(avgDirectEnrollees, gen), members);
    const earningsAtGen = enrolleesAtGen * avgMatrixPerMember;
    const eligibleMembers = members * pctEligible;
    total += eligibleMembers * rate * earningsAtGen;
  }
  return total;
}

const OrbitAdminSimulatorPage = () => {
  const { language } = useLanguage();
  const t = (fr, en) => language === 'fr' ? fr : en;

  // Matrix dimensions
  const [matrixWidth, setMatrixWidth] = useState(2);
  const [matrixDepth, setMatrixDepth] = useState(15);

  // Financial params
  const [membership, setMembership] = useState(14);
  const [reserveType, setReserveType] = useState('flat'); // 'flat' or 'percent'
  const [reserveValue, setReserveValue] = useState(4);
  const [affiliateFee, setAffiliateFee] = useState(49.95);
  const [commRate, setCommRate] = useState(2.5);
  const [affiliatePercent, setAffiliatePercent] = useState(100);
  const [fillPercent, setFillPercent] = useState(80);

  // Growth params
  const [startingMembers, setStartingMembers] = useState(100);
  const [monthlyGrowthRate, setMonthlyGrowthRate] = useState(15);
  const [churnRate, setChurnRate] = useState(3);
  const [avgNewRecruits, setAvgNewRecruits] = useState(2);
  const [projectionYears, setProjectionYears] = useState(3);

  // Rank distribution
  const [rankDist, setRankDist] = useState(DEFAULT_RANK_DIST.map(r => ({ ...r })));

  // UI state
  const [results, setResults] = useState(null);
  const [calculated, setCalculated] = useState(false);
  const [activeTab, setActiveTab] = useState('annual');
  const [showRanks, setShowRanks] = useState(false);
  const [expandedYear, setExpandedYear] = useState(null);

  const updateRankPct = (idx, val) => {
    const updated = [...rankDist];
    updated[idx] = { ...updated[idx], pct: Number(val) };
    setRankDist(updated);
  };

  const totalRankPct = rankDist.reduce((s, r) => s + r.pct, 0);

  // Derived
  const reservePerMember = useMemo(() =>
    reserveType === 'flat' ? reserveValue : membership * reserveValue / 100
  , [reserveType, reserveValue, membership]);

  const netAmount = useMemo(() => membership - reservePerMember, [membership, reservePerMember]);

  const maxCapacity = useMemo(() => getMaxCapacity(matrixWidth, matrixDepth), [matrixWidth, matrixDepth]);

  // === MAIN PROJECTION ENGINE ===
  const simulate = useCallback(() => {
    const commPerMember = netAmount * commRate / 100;
    const fsBonusPerRecruit = 25.00; // $5 + $20 standard LiveGood
    const months = [];

    for (let m = 1; m <= projectionYears * 12; m++) {
      let members, newMembers, churned;

      if (m === 1) {
        members = startingMembers;
        newMembers = 0;
        churned = 0;
      } else {
        const prev = months[m - 2];
        newMembers = Math.floor(prev.members * monthlyGrowthRate / 100);
        churned = Math.floor(prev.members * churnRate / 100);
        members = prev.members + newMembers - churned;
      }

      // Cap at matrix capacity
      members = Math.min(members, maxCapacity);
      if (members < 1) members = 1;

      const totalAffiliates = Math.round(newMembers * (affiliatePercent / 100));

      // Revenue
      const membershipRev = members * membership;
      const affiliateRev = totalAffiliates * affiliateFee;
      const totalRev = membershipRev + affiliateRev;

      // Reserve
      const totalReserve = members * reservePerMember;

      // Commissions (calculated on NET amount)
      const matrixComm = calcMatrixComm(members, commPerMember, fillPercent, matrixWidth, matrixDepth, rankDist, totalRankPct);

      // Fast start based on new recruits this month
      const monthRecruits = newMembers * avgNewRecruits;
      const fsComm = calcFastStart(monthRecruits, fsBonusPerRecruit, rankDist, totalRankPct);

      // Matching
      const matchComm = calcMatching(matrixComm, members, avgNewRecruits, rankDist, totalRankPct);

      const totalComm = matrixComm + fsComm + matchComm;
      const netProfit = totalRev - totalComm;
      const prev = m > 1 ? months[m - 2] : null;

      months.push({
        month: m,
        year: Math.ceil(m / 12),
        members,
        newMembers,
        churned,
        membershipRev,
        affiliateRev,
        totalRev,
        totalReserve,
        matrixComm,
        fsComm,
        matchComm,
        totalComm,
        netProfit,
        cumulProfit: (prev?.cumulProfit || 0) + netProfit,
        cumulReserve: (prev?.cumulReserve || 0) + totalReserve,
        cumulRev: (prev?.cumulRev || 0) + totalRev,
        cumulComm: (prev?.cumulComm || 0) + totalComm,
      });
    }

    // Annual summaries
    const annuals = [];
    for (let y = 1; y <= projectionYears; y++) {
      const ym = months.filter(m => m.year === y);
      if (ym.length === 0) continue;
      const last = ym[ym.length - 1];
      annuals.push({
        year: y,
        startMembers: ym[0].members,
        endMembers: last.members,
        totalRev: ym.reduce((s, m) => s + m.totalRev, 0),
        totalMembershipRev: ym.reduce((s, m) => s + m.membershipRev, 0),
        totalAffiliateRev: ym.reduce((s, m) => s + m.affiliateRev, 0),
        totalReserve: ym.reduce((s, m) => s + m.totalReserve, 0),
        matrixComm: ym.reduce((s, m) => s + m.matrixComm, 0),
        fsComm: ym.reduce((s, m) => s + m.fsComm, 0),
        matchComm: ym.reduce((s, m) => s + m.matchComm, 0),
        totalComm: ym.reduce((s, m) => s + m.totalComm, 0),
        netProfit: ym.reduce((s, m) => s + m.netProfit, 0),
        cumulProfit: last.cumulProfit,
        cumulReserve: last.cumulReserve,
        months: ym,
      });
    }

    // Global totals
    const lastMonth = months[months.length - 1];
    const resultData = {
      months,
      annuals,
      totalRevenue: lastMonth.cumulRev,
      totalCommissions: lastMonth.cumulComm,
      totalReserve: lastMonth.cumulReserve,
      totalProfit: lastMonth.cumulProfit,
      finalMembers: lastMonth.members,
      matrixLabel: `${matrixWidth}x${matrixDepth}`,
      netAmountUsed: netAmount,
      reservePerMemberUsed: reservePerMember,
      projectionYears,
      membership,
      startingMembers,
    };
    setResults(resultData);
    setCalculated(true);
    setActiveTab('annual');

    // Save to localStorage for Cash Flow page
    try {
      localStorage.setItem('orbit_simulator_data', JSON.stringify({ ...resultData, timestamp: new Date().toISOString() }));
    } catch (e) { console.error('Save simulator:', e); }
  }, [startingMembers, membership, reservePerMember, netAmount, affiliateFee, commRate,
      affiliatePercent, fillPercent, monthlyGrowthRate, churnRate, avgNewRecruits,
      projectionYears, matrixWidth, matrixDepth, rankDist, totalRankPct, maxCapacity]);

  return (
    <OrbitLayout>
      <div className="space-y-6" data-testid="orbit-admin-simulator">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
            <Sliders className="w-8 h-8 text-amber-400" />
            {t('Simulateur financier Admin', 'Admin Financial Simulator')}
          </h1>
          <p className="text-gray-300 text-sm mt-1">{t('Projections mensuelles et annuelles avec reserve et croissance', 'Monthly and annual projections with reserve and growth')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ========== LEFT PANEL: PARAMETERS ========== */}
          <div className="lg:col-span-1 space-y-4">

            {/* Matrix Dimensions */}
            <div className="bg-[#111827]/80 border border-amber-500/20 rounded-xl p-5 space-y-4">
              <h2 className="text-base font-semibold text-amber-400 flex items-center gap-2">
                <Planet className="w-4 h-4" />
                {t('Dimensions de la matrice', 'Matrix Dimensions')}
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('Largeur', 'Width')}</label>
                  <input type="number" min="2" max="10" value={matrixWidth} onChange={e => setMatrixWidth(Math.max(2, Number(e.target.value)))}
                    className="w-full px-3 py-2.5 bg-[#1E293B] border border-amber-500/20 rounded-lg text-white text-sm text-center font-bold"
                    data-testid="sim-width" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('Profondeur', 'Depth')}</label>
                  <input type="number" min="1" max="20" value={matrixDepth} onChange={e => setMatrixDepth(Math.max(1, Number(e.target.value)))}
                    className="w-full px-3 py-2.5 bg-[#1E293B] border border-amber-500/20 rounded-lg text-white text-sm text-center font-bold"
                    data-testid="sim-depth" />
                </div>
              </div>
              <div className="bg-amber-500/10 rounded-lg p-2 text-center">
                <span className="text-amber-400 font-bold text-lg">{matrixWidth}x{matrixDepth}</span>
                <span className="text-gray-500 text-xs ml-2">(max {maxCapacity.toLocaleString()} {t('places', 'slots')})</span>
              </div>
            </div>

            {/* Financial Parameters + Reserve */}
            <div className="bg-[#111827]/80 border border-indigo-500/20 rounded-xl p-5 space-y-4">
              <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <CurrencyDollar className="w-4 h-4 text-indigo-400" />
                {t('Parametres financiers', 'Financial Parameters')}
              </h2>

              {/* Membership + Reserve */}
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('Abonnement mensuel', 'Monthly membership')}</label>
                <div className="relative">
                  <input type="number" step="0.01" min="0" value={membership} onChange={e => setMembership(Number(e.target.value))}
                    className="w-full px-3 py-2.5 bg-[#1E293B] border border-indigo-500/20 rounded-lg text-white text-sm pr-7"
                    data-testid="sim-membership" />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">$/mo</span>
                </div>
              </div>

              {/* Reserve Config */}
              <div className="bg-[#0D1321] border border-amber-500/15 rounded-lg p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-xs text-amber-400 font-semibold flex items-center gap-1.5">
                    <PiggyBank className="w-3.5 h-3.5" />
                    {t('Reserve', 'Reserve')}
                  </label>
                  <div className="flex bg-[#1E293B] rounded-md overflow-hidden text-xs">
                    <button onClick={() => setReserveType('flat')}
                      className={`px-2.5 py-1 transition-colors ${reserveType === 'flat' ? 'bg-amber-600 text-white' : 'text-gray-400 hover:text-white'}`}
                      data-testid="sim-reserve-flat">$</button>
                    <button onClick={() => setReserveType('percent')}
                      className={`px-2.5 py-1 transition-colors ${reserveType === 'percent' ? 'bg-amber-600 text-white' : 'text-gray-400 hover:text-white'}`}
                      data-testid="sim-reserve-pct">%</button>
                  </div>
                </div>
                <div className="relative">
                  <input type="number" step="0.01" min="0" value={reserveValue}
                    onChange={e => setReserveValue(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-[#1E293B] border border-amber-500/20 rounded-lg text-white text-sm pr-12"
                    data-testid="sim-reserve-value" />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-amber-400 text-xs font-medium">
                    {reserveType === 'flat' ? '$/mo' : '%'}
                  </span>
                </div>
                {/* Net amount display */}
                <div className="flex justify-between items-center text-xs bg-emerald-500/10 rounded px-2.5 py-1.5">
                  <span className="text-gray-400">{t('Montant net (commissions)', 'Net amount (commissions)')}</span>
                  <span className="text-emerald-400 font-bold" data-testid="sim-net-amount">
                    {formatMoney(netAmount, language)} / {t('mois', 'mo')}
                  </span>
                </div>
                <p className="text-[10px] text-gray-400">{membership}$ - {reservePerMember.toFixed(2)}$ {t('reserve', 'reserve')} = {netAmount.toFixed(2)}$ {t('pour les commissions', 'for commissions')}</p>
              </div>

              {/* Affiliate fee + Comm rate */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('Frais affiliation', 'Affiliate fee')}<span className="ml-1 text-[10px] text-amber-400">{t('(unique)', '(one-time)')}</span></label>
                  <div className="relative">
                    <input type="number" step="0.01" min="0" value={affiliateFee} onChange={e => setAffiliateFee(Number(e.target.value))}
                      className="w-full px-3 py-2.5 bg-[#1E293B] border border-indigo-500/20 rounded-lg text-white text-sm pr-7"
                      data-testid="sim-affiliate-fee" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">$</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('Taux commission', 'Comm. rate')}</label>
                  <div className="relative">
                    <input type="number" step="0.1" min="0" max="100" value={commRate} onChange={e => setCommRate(Number(e.target.value))}
                      className="w-full px-3 py-2.5 bg-[#1E293B] border border-indigo-500/20 rounded-lg text-white text-sm pr-7"
                      data-testid="sim-comm-rate" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">%</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('% affilies', '% affiliates')}</label>
                  <div className="relative">
                    <input type="number" min="0" max="100" value={affiliatePercent} onChange={e => setAffiliatePercent(Number(e.target.value))}
                      className="w-full px-3 py-2.5 bg-[#1E293B] border border-indigo-500/20 rounded-lg text-white text-sm pr-7"
                      data-testid="sim-affiliate-pct" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">%</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('Remplissage', 'Fill rate')}: <span className="text-indigo-400 font-bold">{fillPercent}%</span></label>
                  <input type="range" min="1" max="100" value={fillPercent} onChange={e => setFillPercent(Number(e.target.value))}
                    className="w-full accent-indigo-500 mt-2" data-testid="sim-fill-pct" />
                </div>
              </div>
            </div>

            {/* Growth Parameters */}
            <div className="bg-[#111827]/80 border border-emerald-500/20 rounded-xl p-5 space-y-4">
              <h2 className="text-base font-semibold text-emerald-400 flex items-center gap-2">
                <TrendUp className="w-4 h-4" />
                {t('Croissance & Projection', 'Growth & Projection')}
              </h2>

              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('Membres de depart', 'Starting members')}</label>
                <input type="number" min="1" value={startingMembers} onChange={e => setStartingMembers(Math.max(1, Number(e.target.value)))}
                  className="w-full px-3 py-2.5 bg-[#1E293B] border border-emerald-500/20 rounded-lg text-white text-sm"
                  data-testid="sim-starting-members" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('Croissance/mois', 'Growth/month')}</label>
                  <div className="relative">
                    <input type="number" step="0.5" min="0" max="200" value={monthlyGrowthRate}
                      onChange={e => setMonthlyGrowthRate(Number(e.target.value))}
                      className="w-full px-3 py-2.5 bg-[#1E293B] border border-emerald-500/20 rounded-lg text-white text-sm pr-7"
                      data-testid="sim-growth-rate" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-emerald-400 text-xs">%</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('Annulations/mois', 'Cancellations/mo')}</label>
                  <div className="relative">
                    <input type="number" step="0.5" min="0" max="100" value={churnRate}
                      onChange={e => setChurnRate(Number(e.target.value))}
                      className="w-full px-3 py-2.5 bg-[#1E293B] border border-emerald-500/20 rounded-lg text-white text-sm pr-7"
                      data-testid="sim-churn-rate" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-400 text-xs">%</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('Moy. recrutes/membre', 'Avg recruits/member')}</label>
                <input type="number" step="0.1" min="0" value={avgNewRecruits} onChange={e => setAvgNewRecruits(Number(e.target.value))}
                  className="w-full px-3 py-2.5 bg-[#1E293B] border border-emerald-500/20 rounded-lg text-white text-sm"
                  data-testid="sim-avg-recruits" />
              </div>

              {/* Projection Horizon */}
              <div>
                <label className="block text-xs text-gray-400 mb-2">
                  {t('Horizon de projection', 'Projection horizon')}: <span className="text-emerald-400 font-bold">{projectionYears} {t('an(s)', 'year(s)')}</span> ({projectionYears * 12} {t('mois', 'months')})
                </label>
                <input type="range" min="1" max="5" value={projectionYears} onChange={e => setProjectionYears(Number(e.target.value))}
                  className="w-full accent-emerald-500" data-testid="sim-projection-years" />
                <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                  <span>1 {t('an', 'yr')}</span><span>2</span><span>3</span><span>4</span><span>5 {t('ans', 'yrs')}</span>
                </div>
              </div>
            </div>

            {/* Rank Distribution (collapsible) */}
            <div className="bg-[#111827]/80 border border-indigo-500/20 rounded-xl overflow-hidden">
              <button onClick={() => setShowRanks(!showRanks)}
                className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                data-testid="sim-toggle-ranks">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-semibold text-white">{t('Distribution des rangs', 'Rank Distribution')}</h2>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${Math.abs(totalRankPct - 100) < 0.5 ? 'text-emerald-400 bg-emerald-500/15' : 'text-red-400 bg-red-500/15'}`}>
                    {totalRankPct}%
                  </span>
                </div>
                {showRanks ? <CaretDown className="w-4 h-4 text-gray-400" /> : <CaretRight className="w-4 h-4 text-gray-400" />}
              </button>
              {showRanks && (
                <div className="px-4 pb-4 space-y-2">
                  {rankDist.map((r, i) => (
                    <div key={r.id} className="flex items-center gap-2">
                      <span className="text-gray-400 text-xs w-16 truncate">{language === 'fr' ? r.fr : r.en}</span>
                      <input type="number" min="0" max="100" value={r.pct}
                        onChange={e => updateRankPct(i, e.target.value)}
                        className="flex-1 px-2 py-1.5 bg-[#1E293B] border border-indigo-500/20 rounded text-white text-sm text-center"
                        data-testid={`sim-rank-${r.id}`} />
                      <span className="text-gray-400 text-xs">%</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* SIMULATE BUTTON */}
            <button onClick={simulate}
              className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-xl hover:from-amber-400 hover:to-orange-400 transition-all shadow-lg shadow-amber-500/25 flex items-center justify-center gap-2 text-lg"
              data-testid="sim-calculate-btn">
              <Play className="w-6 h-6" weight="fill" />
              {t('Simuler', 'Simulate')}
            </button>
          </div>

          {/* ========== RIGHT PANEL: RESULTS ========== */}
          {calculated && results ? (
          <div className="lg:col-span-2 space-y-5">

            {/* Global Status Banner */}
            <div className={`rounded-xl p-5 border ${results.totalProfit >= 0
              ? 'bg-emerald-500/10 border-emerald-500/20'
              : 'bg-red-500/10 border-red-500/20'}`}>
              <div className="flex items-start gap-4">
                {results.totalProfit >= 0
                  ? <CheckCircle className="w-8 h-8 text-emerald-400 flex-shrink-0 mt-0.5" weight="fill" />
                  : <Warning className="w-8 h-8 text-red-400 flex-shrink-0 mt-0.5" weight="fill" />}
                <div className="flex-1">
                  <h3 className={`text-xl font-bold ${results.totalProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {results.totalProfit >= 0
                      ? t('SCENARIO RENTABLE', 'PROFITABLE SCENARIO')
                      : t('SCENARIO DEFICITAIRE', 'DEFICIT SCENARIO')}
                  </h3>
                  <p className="text-gray-400 text-sm mt-1">
                    {results.matrixLabel} | {startingMembers.toLocaleString()} {t('depart', 'start')} → {results.finalMembers.toLocaleString()} {t('membres', 'members')} | {projectionYears} {t('an(s)', 'year(s)')}
                  </p>
                  <p className="text-gray-500 text-xs mt-0.5">
                    {t('Base commissions', 'Commission base')}: {formatMoney(results.netAmountUsed, language)} ({membership}$ - {formatMoney(results.reservePerMemberUsed, language)} {t('reserve', 'reserve')})
                  </p>
                </div>
                <div className="text-right">
                  <p className={`text-3xl font-bold ${results.totalProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}
                    data-testid="sim-total-profit">
                    {formatMoney(results.totalProfit, language, 0)}
                  </p>
                  <p className="text-gray-500 text-xs">{t('Profit cumule', 'Cumulative profit')} ({projectionYears} {t('an(s)', 'yr(s)')})</p>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-[#111827]/80 border border-emerald-500/20 rounded-xl p-4 text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1.5">
                  <ArrowDown className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-[10px] text-gray-400 uppercase">{t('Revenus totaux', 'Total Revenue')}</span>
                </div>
                <p className="text-xl font-bold text-emerald-400">{formatMoney(results.totalRevenue, language, 0)}</p>
              </div>
              <div className="bg-[#111827]/80 border border-amber-500/20 rounded-xl p-4 text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1.5">
                  <PiggyBank className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-[10px] text-gray-400 uppercase">{t('Reserve cumulee', 'Cumul. Reserve')}</span>
                </div>
                <p className="text-xl font-bold text-amber-400">{formatMoney(results.totalReserve, language, 0)}</p>
              </div>
              <div className="bg-[#111827]/80 border border-red-500/20 rounded-xl p-4 text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1.5">
                  <ArrowUp className="w-3.5 h-3.5 text-red-400" />
                  <span className="text-[10px] text-gray-400 uppercase">{t('Commissions', 'Commissions')}</span>
                </div>
                <p className="text-xl font-bold text-red-400">{formatMoney(results.totalCommissions, language, 0)}</p>
              </div>
              <div className="bg-[#111827]/80 border border-indigo-500/20 rounded-xl p-4 text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1.5">
                  <Minus className="w-3.5 h-3.5 text-indigo-400" />
                  <span className="text-[10px] text-gray-400 uppercase">{t('Profit net', 'Net Profit')}</span>
                </div>
                <p className={`text-xl font-bold ${results.totalProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {formatMoney(results.totalProfit, language, 0)}
                </p>
              </div>
            </div>

            {/* Tab Switcher */}
            <div className="flex gap-1 bg-[#111827]/80 rounded-lg p-1 border border-indigo-500/10">
              <button onClick={() => setActiveTab('annual')}
                className={`flex-1 py-2.5 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2
                  ${activeTab === 'annual' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                data-testid="sim-tab-annual">
                <Calendar className="w-4 h-4" />
                {t('Vue annuelle', 'Annual View')}
              </button>
              <button onClick={() => setActiveTab('monthly')}
                className={`flex-1 py-2.5 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2
                  ${activeTab === 'monthly' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                data-testid="sim-tab-monthly">
                <ChartBar className="w-4 h-4" />
                {t('Vue mensuelle', 'Monthly View')}
              </button>
            </div>

            {/* ===== ANNUAL VIEW ===== */}
            {activeTab === 'annual' && (
            <div className="space-y-4">
              {results.annuals.map(yr => {
                const isExpanded = expandedYear === yr.year;
                const margin = yr.totalRev > 0 ? (yr.netProfit / yr.totalRev * 100) : 0;
                return (
                <div key={yr.year} className="bg-[#111827]/80 border border-indigo-500/10 rounded-xl overflow-hidden">
                  {/* Year Header */}
                  <button onClick={() => setExpandedYear(isExpanded ? null : yr.year)}
                    className="w-full p-5 hover:bg-white/5 transition-colors"
                    data-testid={`sim-year-${yr.year}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg
                          ${yr.netProfit >= 0 ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                          {t('A', 'Y')}{yr.year}
                        </div>
                        <div className="text-left">
                          <h3 className="text-white font-semibold">{t('Annee', 'Year')} {yr.year}</h3>
                          <p className="text-gray-500 text-xs">
                            {yr.startMembers.toLocaleString()} → {yr.endMembers.toLocaleString()} {t('membres', 'members')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right hidden sm:block">
                          <p className="text-emerald-400 text-sm">{formatMoney(yr.totalRev, language, 0)}</p>
                          <p className="text-gray-400 text-[10px] uppercase">{t('Revenus', 'Revenue')}</p>
                        </div>
                        <div className="text-right hidden sm:block">
                          <p className="text-amber-400 text-sm">{formatMoney(yr.totalReserve, language, 0)}</p>
                          <p className="text-gray-400 text-[10px] uppercase">{t('Reserve', 'Reserve')}</p>
                        </div>
                        <div className="text-right hidden sm:block">
                          <p className="text-red-400 text-sm">{formatMoney(yr.totalComm, language, 0)}</p>
                          <p className="text-gray-400 text-[10px] uppercase">{t('Commissions', 'Commissions')}</p>
                        </div>
                        <div className="text-right">
                          <p className={`text-lg font-bold ${yr.netProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {formatMoney(yr.netProfit, language, 0)}
                          </p>
                          <p className="text-gray-400 text-[10px]">{t('Marge', 'Margin')}: {margin.toFixed(1)}%</p>
                        </div>
                        {isExpanded ? <CaretDown className="w-5 h-5 text-gray-400" /> : <CaretRight className="w-5 h-5 text-gray-400" />}
                      </div>
                    </div>
                  </button>

                  {/* Expanded Year Details */}
                  {isExpanded && (
                  <div className="border-t border-indigo-500/10">
                    {/* Year breakdown */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4">
                      <div className="bg-[#0D1321] rounded-lg p-3">
                        <p className="text-[10px] text-gray-500 uppercase mb-1">{t('Abonnements', 'Memberships')} <span className="text-indigo-400">{t('mensuel', 'monthly')}</span></p>
                        <p className="text-white font-medium text-sm">{formatMoney(yr.totalMembershipRev, language, 0)}</p>
                      </div>
                      <div className="bg-[#0D1321] rounded-lg p-3">
                        <p className="text-[10px] text-gray-500 uppercase mb-1">{t('Affiliations', 'Affiliate fees')} <span className="text-amber-400">{t('unique', 'one-time')}</span></p>
                        <p className="text-white font-medium text-sm">{formatMoney(yr.totalAffiliateRev, language, 0)}</p>
                      </div>
                      <div className="bg-[#0D1321] rounded-lg p-3">
                        <p className="text-[10px] text-gray-500 uppercase mb-1">{t('Reserve annuelle', 'Annual reserve')}</p>
                        <p className="text-amber-400 font-medium text-sm">{formatMoney(yr.totalReserve, language, 0)}</p>
                      </div>
                      <div className="bg-[#0D1321] rounded-lg p-3">
                        <p className="text-[10px] text-gray-500 uppercase mb-1">{t('Comm. Matrice', 'Matrix Comm.')}</p>
                        <p className="text-white font-medium text-sm">{formatMoney(yr.matrixComm, language, 0)}</p>
                      </div>
                      <div className="bg-[#0D1321] rounded-lg p-3">
                        <p className="text-[10px] text-gray-500 uppercase mb-1">Fast Start</p>
                        <p className="text-white font-medium text-sm">{formatMoney(yr.fsComm, language, 0)}</p>
                      </div>
                      <div className="bg-[#0D1321] rounded-lg p-3">
                        <p className="text-[10px] text-gray-500 uppercase mb-1">Matching Bonus</p>
                        <p className="text-white font-medium text-sm">{formatMoney(yr.matchComm, language, 0)}</p>
                      </div>
                    </div>
                    {/* Monthly mini-table for this year */}
                    <div className="overflow-x-auto px-4 pb-4">
                      <table className="w-full text-xs">
                        <thead className="text-gray-500 uppercase border-b border-indigo-500/10">
                          <tr>
                            <th className="px-2 py-1.5 text-left">{t('Mois', 'Month')}</th>
                            <th className="px-2 py-1.5 text-right">{t('Mbrs', 'Mbrs')}</th>
                            <th className="px-2 py-1.5 text-right">{t('+Nouv', '+New')}</th>
                            <th className="px-2 py-1.5 text-right">{t('Revenus', 'Revenue')}</th>
                            <th className="px-2 py-1.5 text-right">{t('Reserve', 'Reserve')}</th>
                            <th className="px-2 py-1.5 text-right">{t('Comm.', 'Comm.')}</th>
                            <th className="px-2 py-1.5 text-right">{t('Profit', 'Profit')}</th>
                            <th className="px-2 py-1.5 text-right">{t('Cumul', 'Cumul.')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-indigo-500/5">
                          {yr.months.map(mo => (
                          <tr key={mo.month} className="hover:bg-white/5">
                            <td className="px-2 py-1.5 text-indigo-400 font-medium">M{mo.month}</td>
                            <td className="px-2 py-1.5 text-right text-white">{mo.members.toLocaleString()}</td>
                            <td className="px-2 py-1.5 text-right text-emerald-400">+{mo.newMembers.toLocaleString()}</td>
                            <td className="px-2 py-1.5 text-right text-gray-300">{formatMoney(mo.totalRev, language, 0)}</td>
                            <td className="px-2 py-1.5 text-right text-amber-400">{formatMoney(mo.totalReserve, language, 0)}</td>
                            <td className="px-2 py-1.5 text-right text-red-400">{formatMoney(mo.totalComm, language, 0)}</td>
                            <td className={`px-2 py-1.5 text-right font-medium ${mo.netProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {formatMoney(mo.netProfit, language, 0)}
                            </td>
                            <td className={`px-2 py-1.5 text-right ${mo.cumulProfit >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                              {formatMoney(mo.cumulProfit, language, 0)}
                            </td>
                          </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  )}
                </div>
                );
              })}

              {/* Cumulative Totals at bottom */}
              <div className={`rounded-xl p-5 border ${results.totalProfit >= 0 ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-gray-400 text-sm">{t('Total sur', 'Total over')} {projectionYears} {t('an(s)', 'year(s)')}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatMoney(results.totalRevenue, language, 0)} {t('revenus', 'revenue')} - {formatMoney(results.totalCommissions, language, 0)} {t('commissions', 'commissions')}
                    </p>
                    <p className="text-xs text-amber-400 mt-0.5">
                      {t('Dont', 'Of which')} {formatMoney(results.totalReserve, language, 0)} {t('en reserve', 'in reserve')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`text-3xl font-bold ${results.totalProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}
                      data-testid="sim-cumul-profit">
                      {formatMoney(results.totalProfit, language, 0)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('Marge globale', 'Overall margin')}: {results.totalRevenue > 0 ? (results.totalProfit / results.totalRevenue * 100).toFixed(1) : 0}%
                    </p>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* ===== MONTHLY VIEW ===== */}
            {activeTab === 'monthly' && (
            <div className="bg-[#111827]/80 border border-indigo-500/10 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-indigo-500/10 flex items-center justify-between">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <ChartBar className="w-4 h-4 text-indigo-400" />
                  {t('Projection mois par mois', 'Month by Month Projection')}
                </h3>
                <span className="text-xs text-gray-500">{results.months.length} {t('mois', 'months')}</span>
              </div>
              <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="text-gray-500 text-xs uppercase border-b border-indigo-500/10 sticky top-0 bg-[#111827]">
                    <tr>
                      <th className="px-3 py-2.5 text-left">{t('Mois', 'Month')}</th>
                      <th className="px-3 py-2.5 text-right">{t('Membres', 'Members')}</th>
                      <th className="px-3 py-2.5 text-right">{t('+Nouveaux', '+New')}</th>
                      <th className="px-3 py-2.5 text-right">{t('-Annul.', '-Cancel')}</th>
                      <th className="px-3 py-2.5 text-right">{t('Rev. Abo.', 'Memb. Rev.')}</th>
                      <th className="px-3 py-2.5 text-right">{t('Rev. Affil.', 'Affil. Rev.')}</th>
                      <th className="px-3 py-2.5 text-right">{t('Reserve', 'Reserve')}</th>
                      <th className="px-3 py-2.5 text-right">{t('Matrice', 'Matrix')}</th>
                      <th className="px-3 py-2.5 text-right">FS</th>
                      <th className="px-3 py-2.5 text-right">Match.</th>
                      <th className="px-3 py-2.5 text-right">{t('Profit', 'Profit')}</th>
                      <th className="px-3 py-2.5 text-right">{t('Cumul', 'Cumul.')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-indigo-500/5">
                    {results.months.map(mo => {
                      const isYearEnd = mo.month % 12 === 0;
                      return (
                      <tr key={mo.month} className={`hover:bg-white/5 ${isYearEnd ? 'border-b-2 border-indigo-500/20' : ''}`}>
                        <td className="px-3 py-2 text-indigo-400 font-medium whitespace-nowrap">
                          M{mo.month}
                          {mo.month % 12 === 1 && <span className="ml-1 text-[10px] text-gray-400">{t('A', 'Y')}{mo.year}</span>}
                        </td>
                        <td className="px-3 py-2 text-right text-white font-medium">{mo.members.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right text-emerald-400">+{mo.newMembers.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right text-red-400/60">{mo.churned > 0 ? `-${mo.churned.toLocaleString()}` : '—'}</td>
                        <td className="px-3 py-2 text-right text-gray-300">{formatMoney(mo.membershipRev, language, 0)}</td>
                        <td className="px-3 py-2 text-right text-amber-300">{formatMoney(mo.affiliateRev, language, 0)}</td>
                        <td className="px-3 py-2 text-right text-amber-400">{formatMoney(mo.totalReserve, language, 0)}</td>
                        <td className="px-3 py-2 text-right text-gray-400">{formatMoney(mo.matrixComm, language, 0)}</td>
                        <td className="px-3 py-2 text-right text-gray-400">{formatMoney(mo.fsComm, language, 0)}</td>
                        <td className="px-3 py-2 text-right text-gray-400">{formatMoney(mo.matchComm, language, 0)}</td>
                        <td className={`px-3 py-2 text-right font-medium ${mo.netProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {formatMoney(mo.netProfit, language, 0)}
                        </td>
                        <td className={`px-3 py-2 text-right font-medium ${mo.cumulProfit >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                          {formatMoney(mo.cumulProfit, language, 0)}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            )}

          </div>
          ) : (
          <div className="lg:col-span-2 flex items-center justify-center">
            <div className="text-center py-20 px-8">
              <ChartBar className="w-20 h-20 text-amber-500/15 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">{t('Configurez vos parametres et cliquez sur', 'Configure your parameters and click')}</p>
              <p className="text-amber-400 font-bold text-2xl mt-2">{t('Simuler', 'Simulate')}</p>
              <p className="text-gray-400 text-sm mt-3">{t('Projections mensuelles et annuelles avec reserve integree', 'Monthly and annual projections with integrated reserve')}</p>
            </div>
          </div>
          )}
        </div>
      </div>
    </OrbitLayout>
  );
};

export default OrbitAdminSimulatorPage;
